import React, {useEffect, useState, Fragment, useContext, useCallback} from 'react'
import { constants } from '../../constants'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import { Button, Icon, Tab } from 'semantic-ui-react'
import { find, map } from 'lodash'
import ConfirmModal from '../partials/ConfirmModal'
import DebouncedSearchInput from '../partials/DebouncedSearchInput'
import { useTranslation } from 'react-i18next'
import PaginationContainer from '../partials/PaginationContainer'
import LinkGroupsModal from '../partials/LinkGroupsModal'
import SuperTable from '../partials/SuperTable'
import SuperTPagination from '../partials/SuperTPagination'
import { v4 } from 'uuid'
import TableSelectionIndicator from '../partials/TableSelectionIndicator'
import { tableActions } from '../../actions/tableActions'
import { dynamicChannelsActions } from '../../actions/dynamicChannelsActions'
import routes from '../../routes/route-constants'
import { push } from 'connected-react-router'
import LimitIndicator from '../tiering/LimitIndicator'
import Messages from '../partials/Messages'
import { tieringActions } from '../../actions/tieringActions'
import DefaultsContext from '../../context/DefaultsContext'
import { appendCommunity } from '../../utils/helper'

function ManageDynamicChannels(props) {

    const communityId = useSelector(state => state.navigation.communityId)
    const d6PlusEnabled = useSelector(state => state.navigation?.community?.d6_plus_enabled)
    const [confirm, setConfirm] = useState({
        showModal: false,
        onConfirm: () => {},
        onClose: () => {},
        message: "",
        header: "",
        showConfirm: true,
        confirmText: null,
        confirmClassName: null,
    })

    const dispatch = useDispatch()
    const dynamicChannelsState = useSelector(state => state.dynamicChannels)
    const context = useContext(DefaultsContext)
    const [tableId] = useState(v4())

    const { t } = useTranslation()
    
    const [filterPayload, setFilterPayload] = useState({
        ...props.defaults
    })
    
    const [selectedGroups, setSelectedGroups] = useState([])
    const [showLinkGroupsModal, setShowLinkGroupsModal] = useState(false)

    const actionFunctions = {
        [constants.MENU_ACTION_DELETE]: () => {
            setConfirm({
                showModal: true,
                onConfirm: () => handleCloseConfirm(), // Just close the modal, no actual unlinking
                onClose: () => handleCloseConfirm(),
                message: t('dynamicGroups.unlink.modal.message'),
                header: t('dynamicGroups.unlink.modal.header'),
                showConfirm: false, // Hide the confirm button, only show dismiss
                confirmText: null,
                confirmClassName: null,
            })
        },
        [constants.MENU_ACTION_REMOVE] : ()=>{},
        [constants.MENU_ACTION_DISABLE] : ()=>{},
        [constants.MENU_ACTION_ENABLE] : ()=>{},
        [constants.MENU_ACTION_COPY] : ()=>{}
    }

    // Table config for dynamic groups
    const headers = [
        {
            name: constants.TABLE_KEY_CHECKBOX,
            key: {
                name: constants.TABLE_KEY_CHECKBOX,
                type: constants.TABLE_KEY_CHECKBOX,
            }
        },
        {
            name: t("channel.manage.table.header.channelName"), // Reusing translation for now
            sortable: true,
            classNames: 'channel-name', 
            key: {
                name: "name",
                classNames: 'channel-name',
            }
        },
        {
            name: t("channel.manage.table.header.members"),
            sortable: true,
            classNames: 'members', 
            key: {
                name: "num_members",
                classNames: 'members',
            }
        }, 
        {
            name: "", 
            key: {
                classNames: 'actions',
                name: constants.TABLE_KEY_ACTIONS,
                type: constants.TABLE_KEY_ACTIONS
            },
        }
    ]

    const panes = [
        {
            menuItem: t('channels.ManageChannels.tabs.groups', 'Groups'),
        },
        {
            menuItem: t('channels.ManageChannels.tabs.dynamicGroups', 'd6+ Groups'),
        }
    ]

    // Cleanup
    useEffect(() => {
        return () => {
            dispatch(dynamicChannelsActions.clearMessages())
            dispatch(tieringActions.reset())
        }
    }, [])

    // Fetch dynamic channels using Redux actions (following ChannelsTab pattern)
    useEffect(() => {
        
        if (!filterPayload.communityId) return;

        context.initDefaults(filterPayload)

        dispatch(dynamicChannelsActions.start())
        dispatch(dynamicChannelsActions.fetchDynamicChannels(filterPayload))

        dispatch(tieringActions.getChannelPlanLimits({
            communityId: filterPayload.communityId
        }))

    }, [dispatch, filterPayload, filterPayload.communityId])

    // When we receive a refresh flag from the messages
    useEffect(() => {
        
        if (!dynamicChannelsState.messages) return;

        if (dynamicChannelsState.messages.refreshData && filterPayload.communityId) {
            dispatch(dynamicChannelsActions.start())
            dispatch(dynamicChannelsActions.fetchDynamicChannels(filterPayload))

            dispatch(tieringActions.getChannelPlanLimits({
                communityId: filterPayload.communityId
            }))
            
            // Clear Selection
            dispatch(tableActions.clearSelection({
                tableId: tableId,
            }))
        }

    }, [dynamicChannelsState.messages])
    

    // Update community filter when community changes (following ChannelsTab pattern)
    useEffect(() => {
        if (!communityId) return;

        if (!d6PlusEnabled) {
            dispatch(push(appendCommunity(communityId, routes.private.CHANNELS)))
            return;
        }

        setFilterPayload(prevFilterPayload => ({...prevFilterPayload, communityId: communityId}))
    }, [communityId])

    const handlePageChange = (page) => {
        setFilterPayload(prevFilterPayload => ({...prevFilterPayload, page: page}))
    }
    
    const handlePaginationIntervalChange = (_perPage) => {
        const perPage  = find(constants.PAGINATION_DEFAULTS, {value: _perPage})
        setFilterPayload(prevFilterPayload => ({...prevFilterPayload, perPage: perPage}))
    }
    
    const handleSortChange = (sort) => {
        setFilterPayload(prevFilterPayload => ({...prevFilterPayload, sort: sort}))
    }
    
    const handleSearchChange = (value) => {
        setFilterPayload(prevFilterPayload => ({...prevFilterPayload, search: value, page: 1}))
    }
    
    const handleRowClick = (groupId) => {
        dispatch(dynamicChannelsActions.start())
        dispatch(push(routes.private.CHANNEL_DETAIL
            .replace(":id", groupId)
            .replace(':communityId', communityId)
            .concat("?isDynamic=true") // @TODO: Indicate we are coming from dynamic channels
        ))
    }

    const handleCheckUpdate = useCallback((selectedIds) => {
        setSelectedGroups(selectedIds)
    }, [])

    const confirmUnlinkMany = () => {
        setConfirm({
            showModal: true,
            onConfirm: () => handleCloseConfirm(), // Just close the modal, no actual unlinking
            onClose: () => handleCloseConfirm(),
            message: t('dynamicGroups.unlink.modal.message'),
            header: t('dynamicGroups.unlink.modal.header'),
            showConfirm: false, // Hide the confirm button, only show dismiss
            confirmText: null,
            confirmClassName: null,
        })
    }
    
    const handleCloseConfirm = () => {
        setConfirm({
            showModal: false,
            onConfirm: () => {},
            onClose: () => {},
            message: "",
            header: "",
            confirmClassName: null,
            confirmText: null,
        })
    }
    

    const handleOpenLinkGroupsModal = () => {
        setShowLinkGroupsModal(true)
    }

    const handleCloseLinkGroupsModal = () => {
        setShowLinkGroupsModal(false)
    }

    const handleLinkGroups = () => {
        setShowLinkGroupsModal(false)
    }

    const handleTabChange = (e, {activeIndex}) => {
        if (activeIndex === 0) {
            dispatch(push(routes.private.CHANNELS.replace(':communityId', communityId)))
            return
        }

        dispatch(push(routes.private.DYNAMIC_CHANNELS.replace(':communityId', communityId)))
    }


    return (
        <div className="page">
            {dynamicChannelsState.errors && (
                <Messages
                    onClose={() => dispatch(dynamicChannelsActions.clearMessages())}
                    errors={dynamicChannelsState.errors || ""}
                    errorCode={dynamicChannelsState.errorCode} />
            )}

            <div className='page-caption with-limit-indicators'>
                <h1>{t('channels.ManageChannels.channels')}</h1>
                <LimitIndicator
                    headerI18nKey='tiering.message.channels.header.limitReached'
                    messageI18nKey='tiering.message.channels.message.limitReached'
                    currentPageDescription={t('tiering.message.channels.caption')} />
            </div>
            <ConfirmModal 
                showConfirm={confirm.showConfirm ?? true}
                header={confirm.header}
                open={confirm.showModal}
                onClose={confirm.onClose}
                message={confirm.message}
                onConfirm={confirm.onConfirm}
                confirmText={confirm.confirmText}
                confirmClassName={confirm.confirmClassName}/>

            {showLinkGroupsModal && (
                <LinkGroupsModal
                    isOpen
                    onClose={handleCloseLinkGroupsModal}
                    onLinkGroups={handleLinkGroups}
                />
            )}

            <div className="page-section white-bg">
                <Tab onTabChange={handleTabChange} activeIndex={1} className="custom-tab" menu={{ secondary: true, pointing: true }} panes={panes} />
                
                <PaginationContainer
                    sectionOne={
                        <DebouncedSearchInput 
                            id="groups-search-input" 
                            placeholder={t('DebouncedSearchInput.placeholder')}
                            name="search" 
                            defaultValue={filterPayload.search}
                            onChange={ handleSearchChange } />
                    }
                    sectionTwo={
                        <Fragment>
                            <Button size='tiny' className='button secondary' onClick={handleOpenLinkGroupsModal}>
                                {t('dynamicGroups.buttons.linkGroups')}
                            </Button>
                        </Fragment>
                    }
                    sectionFive={
                        <Fragment>
                            <Button 
                                disabled={selectedGroups.length === 0} 
                                size='tiny' 
                                className='button tertiary'
                                onClick={confirmUnlinkMany}>
                                <Icon name='unlink' />{t('dynamicGroups.buttons.unlink')}
                            </Button>

                            {selectedGroups.length > 0 && (
                                <TableSelectionIndicator
                                    items={selectedGroups.length}
                                    label={t("table.selection.dynamicGroup.label")}
                                    labelPlural={t("table.selection.dynamicGroup.labelPlural")}
                                    onClear={() => dispatch(tableActions.clearSelection({
                                        tableId: tableId,
                                    }))} />
                            )}
                        </Fragment>
                    }
                    sectionSix={
                        <SuperTPagination
                            spacer
                            totalCount={ dynamicChannelsState.pagination?.total || 0 } 
                            pageItemsCount={ dynamicChannelsState.pagination?.count || 0 } 
                            totalPages={ dynamicChannelsState.pagination?.total_pages || 0 } 
                            currentPage={ dynamicChannelsState.pagination?.current_page|| 0 }
                            onPageChange={ handlePageChange }
                            onPaginationIntervalChange={ handlePaginationIntervalChange }
                            perPageMenu={ constants.PAGINATION_DEFAULTS }
                            defaultValue={ filterPayload.perPage?.value || 25 }/>
                    } />

                <div className="table-container">
                    <SuperTable
                        id={tableId}
                        classNames={["super-t__dynamic-groups"]}
                        loading={ dynamicChannelsState.loading }
                        headers={ headers }
                        keys={ map(headers, "key") }
                        records={ dynamicChannelsState.dynamicChannelsTransformed || [] }
                        onRecordClick={handleRowClick}
                        onSortChange={ handleSortChange }
                        noRecordsMessage={t('dynamicGroups.table.noRecordsMessage')}
                        actionFunctions={
                            actionFunctions
                        }
                        onCheckUpdate={ handleCheckUpdate }
                        sortBy={ filterPayload.sort }/>
                </div>
            </div>
        </div>
    )
}

export default ManageDynamicChannels
